import React, { useState, useRef } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeEmail, sanitizeText } from '../../utils/inputSanitizer';
import { createAdminNotificationMeta } from '../../utils/adminNotificationMeta';

function ContactSupport() {
  const { user } = useAuth();
  const formRef = useRef();
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const fd = new FormData(formRef.current);
      const sanitizedName = sanitizeText(fd.get('name') ?? '');
      const submissionData = {
        name: sanitizedName,
        email: sanitizeEmail(fd.get('email') ?? ''),
        message: sanitizeText(fd.get('message') ?? ''),
        subject: sanitizeText(fd.get('subject') ?? '') || 'Research Support Inquiry',
        timestamp: new Date(),
        status: 'pending',
        type: 'research_support',
        userId: user?.uid || null,
        ...createAdminNotificationMeta({
          type: 'research_support_submission',
          summary: `New research support inquiry from ${sanitizedName}`,
        }),
      };
      await addDoc(collection(db, 'contactSubmissions'), submissionData);
      setStatus({
        type: 'success',
        message: 'Your message has been sent. We typically respond within 2–3 business days.',
      });
      formRef.current.reset();
    } catch {
      setStatus({
        type: 'error',
        message: 'Unable to send your message. Please email support@iofellowship.org directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Get in touch</h2>
        <ul className="space-y-4 text-gray-600 text-sm">
          <li>
            <span className="font-medium text-gray-800">Email:</span>{' '}
            <a href="mailto:support@iofellowship.org" className="text-iof hover:underline">
              support@iofellowship.org
            </a>
          </li>
          <li>
            <span className="font-medium text-gray-800">Scholarships desk:</span>{' '}
            <a href="mailto:iofscholarships@gmail.com" className="text-iof hover:underline">
              iofscholarships@gmail.com
            </a>
          </li>
          <li>
            <span className="font-medium text-gray-800">Hours:</span> Monday–Friday, 9:00–17:00 WAT
          </li>
        </ul>
      </div>

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 rounded-lg p-6 space-y-4"
      >
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full name
          </label>
          <input
            id="name"
            name="name"
            required
            defaultValue={user?.displayName || ''}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            defaultValue={user?.email || ''}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          />
        </div>
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            id="subject"
            name="subject"
            placeholder="e.g. Submission status, document requirements"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          />
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          />
        </div>
        {status.message && (
          <p
            className={`text-sm ${
              status.type === 'success' ? 'text-green-700' : 'text-red-600'
            }`}
            role="alert"
          >
            {status.message}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 bg-iof text-white rounded-md font-medium hover:bg-iof-dark disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send message'}
        </button>
      </form>
    </div>
  );
}

export default ContactSupport;
